import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, FileWarning, FolderOpen, Loader2, MessageSquare, Palette, Sparkles, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/auth/auth-provider'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import { useBulkUploadVaultFiles, useIngestionJobs } from '@/features/vault/use-vault'
import { useCreateBrandProfile } from '@/features/settings/use-brand'
import { fetchDemoContent } from '@/features/demo/demo-api'
import { markOnboardingDone } from '@/lib/onboarding'
import type { VaultBulkUploadResponse, VaultBulkUploadResultItem } from '@/features/vault/vault-api'

const steps = ['Workspace', 'Plan', 'Brand', 'Erste Dokumente', 'Start']

// Slug-Regel angelehnt an den Brand-Settings-Dialog (pages/settings/brand.tsx):
// das Backend verlangt ^[a-z0-9-]+$.
function slugifyBrandName(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function OnboardingPage() {
  const [step, setStep] = useState(0)
  const navigate = useNavigate()
  const { user } = useAuth()
  const { workspace } = useWorkspace()
  const [files, setFiles] = useState<File[]>([])
  const [uploadResult, setUploadResult] = useState<VaultBulkUploadResponse | null>(null)
  const upload = useBulkUploadVaultFiles()
  const { data: ingestionData } = useIngestionJobs()

  // Brand-Schritt (optional): Mini-Formular, legt das Default-Brand-Profil an.
  const [brandName, setBrandName] = useState(() => workspace.name ?? '')
  const [brandColor, setBrandColor] = useState('#2F1863')
  const [brandSaved, setBrandSaved] = useState(false)
  const createBrand = useCreateBrandProfile()

  // Demo-Seed (M8.1): Beispiel-Inhalte laufen ueber den normalen
  // Bulk-Upload-Pfad (POST /vault/bulk-upload) inkl. echter Verarbeitung.
  const [demoSeeding, setDemoSeeding] = useState(false)
  const [demoError, setDemoError] = useState<string | null>(null)

  const liveUploadResults = useMemo(() => {
    if (!uploadResult) return null
    const jobs = ingestionData?.jobs ?? []
    return uploadResult.results.map((item) => {
      const job = jobs.find((candidate) =>
        (item.ingestionJobId && candidate.id === item.ingestionJobId)
        || (!item.ingestionJobId && item.path && candidate.vault_file_path === item.path),
      )
      return job ? { ...item, ingestionStatus: job.status } : item
    })
  }, [ingestionData?.jobs, uploadResult])

  const statusLabel = (status: string) =>
    status === 'created'
      ? 'hochgeladen'
      : status === 'overwritten'
        ? 'überschrieben'
        : status === 'skipped'
          ? 'übersprungen'
          : status === 'error'
            ? 'Uploadfehler'
            : status

  async function uploadInitialDocuments() {
    if (files.length === 0) {
      setStep(4)
      return
    }
    setUploadResult(null)
    try {
      const result = await upload.mutateAsync({
        files,
        targetFolder: '_Inbox',
        conflictStrategy: 'rename',
      })
      setUploadResult(result)
      const imported = result.results.filter((item) => item.status === 'created' || item.status === 'overwritten').length
      const errors = result.results.filter((item) => item.status === 'error').length
      const parserErrors = result.results.filter((item) => item.parserError).length
      if (imported > 0) {
        toast.success(`${imported} Dokumente übernommen`)
      }
      if (errors > 0) {
        toast.error(`${errors} Fehler bei Upload`)
      }
      if (parserErrors > 0) {
        toast(`${parserErrors} Datei(en) konnten nicht gelesen werden`, { style: { background: '#fef3c7', color: '#92400e' } })
      }
      // Nur zum nächsten Schritt, wenn mindestens eine Datei erfolgreich war
      // und kein komplettes Versagen vorliegt
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload fehlgeschlagen')
    }
  }

  async function saveBrandProfile() {
    const name = brandName.trim()
    const slug = slugifyBrandName(name)
    if (!name || !slug) return
    try {
      await createBrand.mutateAsync({
        slug,
        name,
        color_primary: brandColor,
        is_default: true,
      })
      setBrandSaved(true)
      toast.success('Brand-Profil gespeichert.')
    } catch (err) {
      // Optionaler Schritt: Fehler nur melden, Weiter bleibt moeglich.
      toast.error(err instanceof Error ? err.message : 'Brand-Profil konnte nicht gespeichert werden')
    }
  }

  async function seedDemoContent() {
    setDemoError(null)
    setDemoSeeding(true)
    const allResults: VaultBulkUploadResultItem[] = []
    try {
      const { files: demoFiles } = await fetchDemoContent()
      const filesByFolder = new Map<string, File[]>()
      for (const demoFile of demoFiles) {
        const list = filesByFolder.get(demoFile.targetFolder) ?? []
        list.push(new File([demoFile.content], demoFile.filename, { type: 'text/markdown' }))
        filesByFolder.set(demoFile.targetFolder, list)
      }
      // Sequenziell pro Zielordner — gleicher Endpunkt wie der manuelle Import,
      // dadurch zeigt die Ergebnisliste den echten Verarbeitungsstatus.
      for (const [targetFolder, folderFiles] of filesByFolder) {
        const result = await upload.mutateAsync({
          files: folderFiles,
          targetFolder,
          conflictStrategy: 'skip',
        })
        allResults.push(...result.results)
      }
      setUploadResult({ results: allResults })
      const imported = allResults.filter((item) => item.status === 'created' || item.status === 'overwritten').length
      const errors = allResults.filter((item) => item.status === 'error').length
      if (imported > 0) {
        toast.success(`${imported} Beispiel-Dokumente übernommen`)
      }
      if (errors > 0) {
        toast.error(`${errors} Fehler bei Upload`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Beispiele konnten nicht geladen werden'
      if (allResults.length > 0) {
        // Teilimport nicht verwerfen: bereits importierte Ordner sichtbar machen.
        setUploadResult({ results: allResults })
        toast.error(`Import teilweise fehlgeschlagen: ${message}`)
      } else {
        setDemoError(message)
        toast.error(message)
      }
    } finally {
      setDemoSeeding(false)
    }
  }

  function finishOnboarding(target: string) {
    markOnboardingDone(user?.id)
    navigate(target, { replace: true })
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center bg-background px-4 py-10">
      <ol aria-label="Einrichtungsschritte" className="mb-6 flex flex-wrap gap-2 rounded-panel border border-border bg-muted/40 p-4">
        {steps.map((label, index) => {
          const done = index < step
          const current = index === step
          return (
            <li key={label} className="flex flex-1 items-center gap-2" aria-current={current ? 'step' : undefined}>
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${done || current ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground'}`}>
                {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
              </span>
              <span className={`text-sm ${current ? 'font-[650] text-foreground' : done ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            </li>
          )
        })}
      </ol>

      <Card>
        <CardHeader>
          <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground" data-testid="onboarding-step-title">{steps[step]}</h1>
          <CardDescription>Ein kurzer Setup-Flow, dann bist du im Chat.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {step === 0 ? (
            <div className="grid gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Name</p>
                <p className="break-words font-medium">{user?.user_metadata?.name ?? user?.email}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Workspace</p>
                <p className="break-words font-medium">{workspace.name}</p>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-2 rounded-md border p-4 text-sm text-muted-foreground">
              <p>
                MindKeep läuft aktuell als funktionierender Prototyp (Case). Die Nutzung ist im
                Prototyp-Betrieb kostenlos — es gibt keinen Bezahlweg und keine Verpflichtung.
              </p>
              <p>
                Dein Workspace startet im Free-Plan mit einem fairen Nutzungskontingent.
              </p>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Dieser Schritt ist optional: Du kannst ihn getrost überspringen und dein Brand-Profil
                später unter Einstellungen → Brand-Profile ergänzen.
              </p>
              <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="onboarding-brand-name">Markenname</Label>
                  <Input
                    id="onboarding-brand-name"
                    data-testid="onboarding-brand-name"
                    value={brandName}
                    onChange={(event) => {
                      setBrandName(event.target.value)
                      setBrandSaved(false)
                    }}
                    placeholder="Name deiner Marke"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="onboarding-brand-color">Primärfarbe</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="onboarding-brand-color"
                      data-testid="onboarding-brand-color"
                      type="color"
                      value={brandColor}
                      onChange={(event) => {
                        setBrandColor(event.target.value)
                        setBrandSaved(false)
                      }}
                      className="h-9 w-16 cursor-pointer rounded-md border border-input bg-background p-1"
                    />
                    <span className="font-mono text-xs text-muted-foreground">{brandColor}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  data-testid="onboarding-brand-save"
                  onClick={() => void saveBrandProfile()}
                  disabled={!brandName.trim() || !slugifyBrandName(brandName) || createBrand.isPending}
                >
                  {createBrand.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Brand-Profil speichern
                </Button>
                {brandSaved ? (
                  <span className="flex items-center gap-1 text-sm text-emerald-600">
                    <Check className="h-4 w-4" />
                    Gespeichert
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="space-y-4">
              {!liveUploadResults ? (
                <>
                  <div className="space-y-3 rounded-md border p-4">
                    <p className="text-sm font-medium">Mit Beispiel-Inhalten starten</p>
                    <p className="text-sm text-muted-foreground">
                      20 kuratierte Beispiel-Dokumente — CEO-Guide, Protokolle, Canvas-Vorlagen.
                      Laufen durch die echte Verarbeitung und sind jederzeit vollständig entfernbar.
                    </p>
                    <div>
                      <Button
                        type="button"
                        data-testid="onboarding-demo-seed"
                        onClick={() => void seedDemoContent()}
                        disabled={demoSeeding || upload.isPending}
                      >
                        {demoSeeding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {demoSeeding ? 'Beispiele werden geladen…' : 'Mit Beispiel-Inhalten starten'}
                      </Button>
                    </div>
                    {demoError ? (
                      <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                        <span className="min-w-0 flex-1">{demoError}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void seedDemoContent()}
                          disabled={demoSeeding}
                        >
                          Nochmal versuchen
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <p className="text-sm font-medium text-muted-foreground">
                    Oder eigene Dokumente hochladen:
                  </p>

                  <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border px-4 py-8 text-sm text-muted-foreground hover:bg-muted">
                    <Upload className="h-5 w-5" />
                    <span>{files.length > 0 ? `${files.length} Datei(en) ausgewählt` : '2-5 erste Dokumente auswählen'}</span>
                    <span className="text-xs">Markdown, Text, PDF, DOCX, XLSX, CSV, PPTX – max. 5 Dateien</span>
                    <input
                      data-testid="onboarding-upload-input"
                      type="file"
                      multiple
                      accept=".md,.markdown,.txt,.pdf,.docx,.xlsx,.csv,.tsv,.pptx"
                      className="hidden"
                      onChange={(event) => setFiles(Array.from(event.target.files ?? []).slice(0, 5))}
                    />
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Dieser Schritt ist optional. Originaldateien auf dem Computer bleiben unverändert.
                    MindKeep analysiert die Dokumente im Hintergrund und ergänzt Tags und Zusammenfassungen.
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => void uploadInitialDocuments()} disabled={upload.isPending || demoSeeding}>
                      {upload.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Dokumente übernehmen
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setStep(4)} disabled={upload.isPending || demoSeeding}>
                      Leer starten
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div
                    data-testid="onboarding-upload-results"
                    className="rounded-md border border-border bg-muted/20 p-3 text-xs space-y-1 max-h-64 overflow-auto"
                  >
                    <p className="text-[11px] text-muted-foreground mb-2">
                      {liveUploadResults.filter((item) => item.status === 'error').length === liveUploadResults.length
                        ? 'Keine Datei konnte importiert werden.'
                        : 'Import abgeschlossen. Die Hintergrundverarbeitung (Analyse) läuft weiter.'}
                    </p>
                    {liveUploadResults.map((item) => (
                      <div key={`${item.filename}-${item.path ?? item.status}`} className="flex items-start justify-between gap-3 py-1.5 border-b border-border/30 last:border-0">
                        <div className="min-w-0 flex-1">
                          <span className="truncate font-mono block text-[11px] leading-tight" title={item.path ?? item.filename}>
                            {item.path ?? item.filename}
                          </span>
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-muted-foreground">
                            {item.parserError && (
                              <span className="text-amber-600 dark:text-amber-400">
                                <FileWarning className="inline h-3 w-3 mr-0.5" />
                                Datei konnte nicht gelesen werden: {item.parserError}
                              </span>
                            )}
                            {item.message && item.status === 'error' && (
                              <span className="text-destructive">{item.message}</span>
                            )}
                            {item.ingestionStatus && (
                              <span className={item.ingestionStatus === 'failed' ? 'text-destructive' : item.ingestionStatus === 'processing' ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}>
                                {item.ingestionStatus === 'completed'
                                  ? 'Verarbeitung abgeschlossen'
                                  : item.ingestionStatus === 'failed'
                                    ? 'Analyse fehlgeschlagen'
                                    : item.ingestionStatus === 'processing'
                                      ? 'Analyse läuft'
                                      : 'Analyse ausstehend'}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className={`shrink-0 text-[10px] font-medium ${
                          item.status === 'error' ? 'text-destructive'
                            : item.status === 'created' || item.status === 'overwritten' ? 'text-emerald-600'
                              : item.status === 'skipped' ? 'text-amber-600'
                                : 'text-muted-foreground'
                        }`}>
                          {statusLabel(item.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {liveUploadResults.filter((item) => item.status === 'created' || item.status === 'overwritten').length > 0 && (
                      <Button type="button" onClick={() => setStep(4)}>
                        Weiter
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setUploadResult(null)
                        setFiles([])
                      }}
                    >
                      {liveUploadResults.filter((item) => item.status === 'created' || item.status === 'overwritten').length > 0
                        ? 'Andere Dateien wählen'
                        : 'Nochmal versuchen'}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setStep(4)} disabled={upload.isPending}>
                      Überspringen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {step === 4 ? (
            <div className="space-y-4">
              <div className="rounded-md border p-4">
                <MessageSquare className="mb-3 h-5 w-5 text-primary" />
                <p className="font-medium">Ersten Test-Chat starten</p>
                <p className="mt-1 text-sm text-muted-foreground">MindKeep ist bereit für deine erste Frage.</p>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Nächste Schritte</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    data-testid="onboarding-next-ask"
                    onClick={() => finishOnboarding('/fragen')}
                    className="flex flex-col gap-1.5 rounded-md border p-3 text-left transition-colors hover:bg-muted"
                  >
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Eine Frage stellen</span>
                    <span className="text-xs text-muted-foreground">Direkt in den Chat mit deinem Wissen.</span>
                  </button>
                  <button
                    type="button"
                    data-testid="onboarding-next-sources"
                    onClick={() => finishOnboarding('/quellen')}
                    className="flex flex-col gap-1.5 rounded-md border p-3 text-left transition-colors hover:bg-muted"
                  >
                    <FolderOpen className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Beispiele und Quellen ansehen</span>
                    <span className="text-xs text-muted-foreground">Die importierten Inhalte im Vault prüfen.</span>
                  </button>
                  <button
                    type="button"
                    data-testid="onboarding-next-brand"
                    onClick={() => finishOnboarding('/einstellungen/brand')}
                    className="flex flex-col gap-1.5 rounded-md border p-3 text-left transition-colors hover:bg-muted"
                  >
                    <Palette className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Brand vervollständigen</span>
                    <span className="text-xs text-muted-foreground">Tonalität und Stil in den Einstellungen.</span>
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep((value) => value - 1)}>
              Zurück
            </Button>
            {step < steps.length - 1 ? (
              <Button type="button" onClick={() => setStep((value) => value + 1)}>
                Weiter
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => finishOnboarding('/fragen')}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Erste Frage stellen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  )
}
